import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Phone, Mail, MapPin, Clock, Truck, Wrench, ShieldCheck,
  Flame, MessageCircle, ExternalLink, ChevronRight, Star,
  CheckCircle2, ArrowRight, Zap, Users, Award, Home, Building2,
  Package, Gauge, HeartHandshake, Sparkles, Quote
} from 'lucide-react';

export default function NESWebsite() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', customerType: '', businessType: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeSegment, setActiveSegment] = useState('all');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Thank you! We will contact you shortly.');
    setFormData({ name: '', email: '', phone: '', customerType: '', businessType: '', message: '' });
    setSubmitting(false);
  };

  // Product catalog from WhatsApp - Real products (45 of 51)
  const products = {
    residential: [
      { 
        name: '14kg LPG Gas Cylinder', 
        desc: 'Oryx Energies - Perfect for family homes', 
        price: 'R483', 
        popular: true,
        bestSeller: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/saqleimj_14kg.png'
      },
      { 
        name: '9kg LPG Gas Cylinder', 
        desc: 'Oryx Energies - Ideal for small households', 
        price: 'R310.50', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/i9679mt5_9kg.png'
      },
      { 
        name: '19kg LPG Gas Cylinder', 
        desc: 'Oryx Energies - Large family size', 
        price: 'R655.50', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/tvkgc9ou_19kg.png'
      },
      { 
        name: '48kg LPG Gas Cylinder', 
        desc: 'Oryx Energies - Commercial/Bulk use', 
        price: 'R1,656', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/10fw8r3o_48kg.png'
      },
      { 
        name: 'Roll About Gas Heater', 
        desc: 'Black - fits 9kg cylinder', 
        price: 'R1,290', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ti50sgus_WhatsApp%20Image%202026-03-17%20at%2010.58.14%20%281%29.jpeg'
      },
      { 
        name: 'Bush Baby Potjie Combo', 
        desc: '3-leg pot + starters + firelighters', 
        price: 'R740', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/4xja5e7q_WhatsApp%20Image%202026-03-17%20at%2010.58.12%20%281%29.jpeg'
      },
      { 
        name: 'Safegas 2 Plate Cooker', 
        desc: 'Auto Ignition Stainless Steel', 
        price: 'R517.50', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ro638ll5_WhatsApp%20Image%202026-03-17%20at%2010.58.11.jpeg'
      },
      { 
        name: 'Portable Gas Stove 1 Plate', 
        desc: 'Safegas portable cooker', 
        price: 'R329', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/1b5gzg4b_WhatsApp%20Image%202026-03-17%20at%2010.58.09.jpeg'
      },
      { 
        name: 'NICE COOKER 2 Burner', 
        desc: 'Fully Automatic Gas Cooker', 
        price: 'R300', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/c5i5h8de_WhatsApp%20Image%202026-03-17%20at%2010.58.10.jpeg'
      },
      { 
        name: "LK's Grid Stand (Chrome)", 
        desc: 'For vertical grid', 
        price: 'R255', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/teyk5h53_WhatsApp%20Image%202026-03-17%20at%2010.58.15.jpeg'
      },
      { 
        name: 'Braai Combo', 
        desc: 'Rooikrans + Charcoal 5kg + 3pc Firelighters', 
        price: 'R110', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/4i9vo37c_WhatsApp%20Image%202026-03-17%20at%2010.58.12.jpeg'
      },
      { 
        name: 'Rooikrans + Charcoal 4kg', 
        desc: 'Wood bag + Bri-Coal', 
        price: 'R95', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/3n19dvkk_WhatsApp%20Image%202026-03-17%20at%2010.58.13%20%282%29.jpeg'
      },
      { 
        name: 'Charcoal 5kg', 
        desc: 'Lekka Braai - Long burning hardwood', 
        price: 'R55', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/uau8gimw_WhatsApp%20Image%202026-03-17%20at%2010.58.07%20%282%29.jpeg'
      },
      { 
        name: 'Gas Canister', 
        desc: 'For camp stove, lamp & blow torch', 
        price: 'R39', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/aasltbyw_WhatsApp%20Image%202026-03-17%20at%2010.58.14.jpeg'
      },
      { 
        name: "LK's Firelighters", 
        desc: '12 Blocks (300g)', 
        price: 'R27.50', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/2ot9v0f5_WhatsApp%20Image%202026-03-17%20at%2010.58.08.jpeg'
      },
      { 
        name: 'Fire Starters', 
        desc: 'Wood kindling for easy braai starting', 
        price: 'R25', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/dv5t1lg3_WhatsApp%20Image%202026-03-17%20at%2011.09.53%20%281%29.jpeg'
      },
      { 
        name: 'Aluminum Tongs (Medium)', 
        desc: 'Strong & sturdy 417mm', 
        price: 'R82.99', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/dr19csus_WhatsApp%20Image%202026-03-17%20at%2010.58.10%20%282%29.jpeg'
      },
      { 
        name: 'Aluminum Tongs (Short)', 
        desc: 'Strong & sturdy 315mm', 
        price: 'R77.50', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/tce65nfw_WhatsApp%20Image%202026-03-17%20at%2010.58.13%20%281%29.jpeg'
      },
      { 
        name: 'Flame Gun', 
        desc: 'Electron striker - burns up to 1300°C', 
        price: 'R85', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/8zf24ded_WhatsApp%20Image%202026-03-17%20at%2010.58.08%20%281%29.jpeg'
      },
      { 
        name: 'Potjie Lid Lifter', 
        desc: "LK's Chrome - Made in SA", 
        price: 'R46', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/p55ts9n3_WhatsApp%20Image%202026-03-17%20at%2010.58.08%20%282%29.jpeg'
      },
      { 
        name: "LK's Coal Scoop", 
        desc: 'Quality coal scoop for braai', 
        price: 'R95', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/rshg5hj1_WhatsApp%20Image%202026-03-17%20at%2011.09.53.jpeg'
      },
      { 
        name: 'Dust Pan (M/S)', 
        desc: 'Metal dust pan for ash cleanup', 
        price: 'R113', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ltepikv4_WhatsApp%20Image%202026-03-17%20at%2011.05.15.jpeg'
      },
      { 
        name: 'Kitchen Paper Towels', 
        desc: 'Nova Jumbo 2Ply 250s', 
        price: 'R80', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/zy7goud7_WhatsApp%20Image%202026-03-17%20at%2010.58.09%20%281%29.jpeg'
      },
      { 
        name: 'Kameldoring 20kg + Charcoal + Firelighters', 
        desc: 'Combo 5: Wood bag + 4kg charcoal + 3pc firelighters', 
        price: 'R175', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ec25ulj8_WhatsApp%20Image%202026-03-17%20at%2010.58.17.jpeg'
      },
      { 
        name: 'Portable Gas Heater', 
        desc: 'Safegas - Excludes gas cylinder', 
        price: 'R350', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/lzozjv44_WhatsApp%20Image%202026-03-17%20at%2010.58.16%20%281%29.jpeg'
      },
      { 
        name: 'Charcoal 4kg', 
        desc: 'Lekka Braai Briquettes - Hardwood, long burning', 
        price: 'R55', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/1qwpzyvh_WhatsApp%20Image%202026-03-17%20at%2010.58.16.jpeg'
      },
      { 
        name: 'Refill Gas Lighter + Free Firelighter', 
        desc: 'Combo 8: Refillable lighter + 1pc firelighter', 
        price: 'R50', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/xjghna3b_WhatsApp%20Image%202026-03-17%20at%2010.58.15%20%282%29.jpeg'
      },
      { 
        name: 'Tjop Rack', 
        desc: "LK's - For crispy chops on the braai", 
        price: 'R195', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/sivew0fr_WhatsApp%20Image%202026-03-17%20at%2010.58.15%20%281%29.jpeg'
      },
      { 
        name: '5kg Charcoal + Spice + Starters Combo', 
        desc: 'Lekka Braai 5kg + 2× Flippen Lekker spice + Fire starters', 
        price: 'R170', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/0jg15ui5_WhatsApp%20Image%202026-03-17%20at%2011.04.01.jpeg'
      },
      { 
        name: 'Flippen Lekker Spice', 
        desc: 'All Purpose Multi-Purpose Spice 200ml', 
        price: 'R45', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/zo0c9msr_WhatsApp%20Image%202026-03-17%20at%2010.58.18%20%281%29.jpeg'
      },
      { 
        name: 'Cooker Top', 
        desc: 'Gas cooker top burner plate', 
        price: 'R175', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/86v9d1h5_WhatsApp%20Image%202026-03-17%20at%2010.58.18.jpeg'
      },
      { 
        name: 'SAFEGAS Butane Gas Cartridge', 
        desc: 'For portable stoves and camping', 
        price: 'R38', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/6zkj27ro_WhatsApp%20Image%202026-03-17%20at%2010.58.17%20%282%29.jpeg'
      },
      { 
        name: 'Bri-Lite Firelighters 3pc', 
        desc: 'Bar-B-Que individually wrapped pieces', 
        price: 'R15', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/3ej5ck01_WhatsApp%20Image%202026-03-17%20at%2010.58.17%20%281%29.jpeg'
      },
      { 
        name: 'Tongs Combo + Firelighters', 
        desc: 'LK\'s Stainless Steel + Aluminum tongs + 3pc firelighters', 
        price: 'R234', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/3ykl6kzl_WhatsApp%20Image%202026-03-17%20at%2011.05.12.jpeg'
      },
      { 
        name: 'Bri-Ketts 4kg', 
        desc: 'Bar-B-Que Namibian Hardwood Briquettes - Extra long burning', 
        price: 'R46', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ks7y3it0_WhatsApp%20Image%202026-03-17%20at%2011.05.11.jpeg'
      },
      { 
        name: 'Blue Gum + Charcoal Combo', 
        desc: '1 bag Blue Gum + Lekka Braai 5kg + Free 1pc firelighter', 
        price: 'R87', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/ybii69ox_WhatsApp%20Image%202026-03-17%20at%2011.04.03.jpeg'
      },
      { 
        name: 'Starters + Charcoal + Firelighters', 
        desc: 'Fire starters + Lekka Braai 5kg + 3pc Bri-Lite', 
        price: 'R95', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/jg8c8xrr_WhatsApp%20Image%202026-03-17%20at%2011.04.02%20%281%29.jpeg'
      },
      { 
        name: 'Blue Gum + Charcoal + 3pc Firelighters', 
        desc: 'Blue Gum wood + Lekka Braai 5kg charcoal + Bri-Lite', 
        price: 'R102', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/w25bnjyt_WhatsApp%20Image%202026-03-17%20at%2011.04.02.jpeg'
      },
      { 
        name: '5kg Charcoal + Potjie Lid Lifter + Starters', 
        desc: 'Lekka Braai 4kg + LK\'s Potjie Lid Lifter + Fire starters', 
        price: 'R125', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/u3mfjpxv_WhatsApp%20Image%202026-03-17%20at%2011.05.14%20%281%29.jpeg'
      },
      { 
        name: 'Die Braai Blaai', 
        desc: 'The perfect blaai for a clean braai - catches drippings', 
        price: 'R120', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/z0tyxfk1_WhatsApp%20Image%202026-03-17%20at%2011.05.14.jpeg'
      },
    ],
    commercial: [
      { 
        name: 'Adaptor Hosetail Chrome', 
        desc: 'C×8mm fitting', 
        price: 'R68', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/kzi70euu_WhatsApp%20Image%202026-03-17%20at%2010.58.11%20%281%29.jpeg'
      },
      { 
        name: 'Adaptor Hosetail Brass', 
        desc: 'C×8mm fitting', 
        price: 'R55', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/3hr4k704_WhatsApp%20Image%202026-03-17%20at%2010.58.12%20%282%29.jpeg'
      },
      { 
        name: 'Hose Kit + 2 Clamps (2m)', 
        desc: 'PVC gas hose kit', 
        price: 'R93', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/8f86e53h_WhatsApp%20Image%202026-03-17%20at%2010.58.10%20%281%29.jpeg'
      },
      { 
        name: 'Low Pressure Regulator Kit', 
        desc: 'Safegas 1.5m hose + 2 clamps', 
        price: 'R185', 
        popular: false,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/34hzimgk_WhatsApp%20Image%202026-03-17%20at%2011.09.53%20%282%29.jpeg'
      },
      { 
        name: 'Bullnose Regulator with Gauge', 
        desc: 'For 9, 14, 19 & 48kg cylinders', 
        price: 'R120', 
        popular: true,
        image: 'https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/xurqceoz_WhatsApp%20Image%202026-03-17%20at%2011.05.15%20%281%29.jpeg'
      },
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/8ym01w4n_WhatsApp%20Image%202026-03-17%20at%2017.42.51.jpeg" 
                alt="North Energy and Supplies Logo"
                className="w-12 h-12 rounded-lg object-contain bg-black"
              />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">North Energy</h1>
                <p className="text-xs text-slate-400 hidden sm:block">& Supplies</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-slate-300 hover:text-orange-400 transition-colors">About</a>
              <a href="#services" className="text-slate-300 hover:text-orange-400 transition-colors">Services</a>
              <a href="#products" className="text-slate-300 hover:text-orange-400 transition-colors">Products</a>
              <a href="#contact" className="text-slate-300 hover:text-orange-400 transition-colors">Contact</a>
            </div>
            
            <div className="flex items-center gap-3">
              <a href="tel:+27752679346" className="hidden md:flex items-center gap-2 text-slate-300 hover:text-orange-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+27 75 267 9346</span>
              </a>
              <a
                href="https://wa.me/27752679346"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                data-testid="whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Order Now</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Serving Both B2C and B2B */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Star className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-orange-300">5-Star Rated on Google</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Quality <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">LPG Gas</span> for Home & Business
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Powering Cape Town homes and businesses with reliable LPG supply. From household cooking to industrial operations - we've got you covered.
            </p>

            {/* Customer Type Toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-slate-800/50 rounded-full p-1 border border-slate-700">
                <button
                  onClick={() => setActiveSegment('home')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                    activeSegment === 'home' 
                      ? 'bg-orange-500 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="segment-home"
                >
                  <Home className="w-4 h-4" />
                  For Home
                </button>
                <button
                  onClick={() => setActiveSegment('business')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                    activeSegment === 'business' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="segment-business"
                >
                  <Building2 className="w-4 h-4" />
                  For Business
                </button>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://wa.me/27752679346?text=Hi%2C%20I%20would%20like%20to%20order%20LPG%20gas" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full" data-testid="order-now-btn">
                  Order Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="tel:+27752679346">
                <Button variant="outline" size="lg" className="border-slate-600 text-slate-200 hover:bg-slate-800 px-8 py-6 text-lg rounded-full" data-testid="call-now-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section - Both B2C and B2B */}
      <section className="py-12 border-y border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '2000+', label: 'Happy Customers', icon: Users, sublabel: 'Homes & Businesses' },
              { number: '8-5', label: 'Delivery Service', icon: Clock, sublabel: '8am to 5pm Daily' },
              { number: '10+', label: 'Years Experience', icon: Award, sublabel: 'Trusted Since 2014' },
              { number: '99%', label: 'On-Time Delivery', icon: Truck, sublabel: 'Reliable Service' }
            ].map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-orange-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
                <div className="text-xs text-slate-500">{stat.sublabel}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500/10 to-blue-500/10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Residential/Home */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-slate-800/50 border-orange-500/30 h-full hover:border-orange-500/50 transition-colors" data-testid="home-card">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                    <Home className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">For Your Home</h3>
                  <p className="text-slate-300 mb-6">
                    Keep your family warm and fed with reliable LPG gas. Perfect for cooking, heating, and braais.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {['Same-day delivery', 'Affordable cylinder prices', 'Safe & reliable', 'Easy exchange program'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="https://wa.me/27752679346?text=Hi%2C%20I%20need%20gas%20for%20my%20home" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                      Order for Home
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>

            {/* Commercial/Business */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-slate-800/50 border-blue-500/30 h-full hover:border-blue-500/50 transition-colors" data-testid="business-card">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                    <Building2 className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">For Your Business</h3>
                  <p className="text-slate-300 mb-6">
                    Power your restaurant, hotel, factory, or commercial operation with bulk LPG solutions.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {['Bulk delivery & pricing', 'Commercial installations', 'Account management', 'Safety compliance'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#contact">
                    <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                      Get Business Quote
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Your Trusted <span className="text-orange-400">LPG Partner</span> in Cape Town
              </h2>
              <p className="text-slate-300 mb-6 text-lg">
                Located at 121 Darwin Rd, Windsor Park, North Energy and Supplies has been serving Cape Town families and businesses since 2014. Whether you need gas for your home braai or your commercial kitchen, we deliver.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Truck, text: 'Delivery Service' },
                  { icon: HeartHandshake, text: 'Family Friendly' },
                  { icon: ShieldCheck, text: 'Safety Certified' },
                  { icon: Gauge, text: 'Competitive Prices' },
                  { icon: Package, text: 'All Cylinder Sizes' },
                  { icon: Sparkles, text: 'Quality Guaranteed' }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-slate-200">
                    <item.icon className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
              
              <a href="https://wa.me/c/27752679346" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="view-catalog-btn">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  View Full Catalog on WhatsApp
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/jk21gkjs_WhatsApp%20Image%202025-07-03%20at%2012.23.19%20%282%29.jpeg"
                  alt="North Energy and Supplies - LP Gas Store Front"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">5.0</div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 sm:py-32 bg-slate-900/50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our <span className="text-orange-400">Services</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Complete LPG solutions for homes and businesses
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Truck,
                title: 'Fast Delivery',
                description: 'Same-day delivery for homes. Scheduled bulk delivery for businesses.',
                badge: 'Most Popular'
              },
              {
                icon: Package,
                title: 'Cylinder Exchange',
                description: 'Easy exchange program. Drop off empty, pick up full. All sizes available.',
                badge: null
              },
              {
                icon: Wrench,
                title: 'Installation',
                description: 'Professional installation for home gas systems and commercial setups.',
                badge: 'Commercial'
              },
              {
                icon: ShieldCheck,
                title: 'Safety Checks',
                description: 'Regular safety inspections and compliance certification for businesses.',
                badge: 'Commercial'
              }
            ].map((service) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800/50 border-slate-700 h-full hover:border-orange-500/50 transition-colors group" data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-6 relative">
                    {service.badge && (
                      <span className={`absolute top-4 right-4 text-xs px-2 py-1 rounded-full ${
                        service.badge === 'Most Popular' 
                          ? 'bg-orange-500/20 text-orange-400' 
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {service.badge}
                      </span>
                    )}
                    <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <service.icon className="w-7 h-7 text-orange-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                    <p className="text-slate-400">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section with Tabs */}
      <section id="products" className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our <span className="text-orange-400">Products</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-6">
              Quality LPG cylinders and accessories for every need
            </p>
            <a
              href="https://wa.me/c/27752679346"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-400 hover:text-green-300"
            >
              <MessageCircle className="w-4 h-4" />
              View Full Catalog on WhatsApp
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 bg-slate-800 mb-8">
              <TabsTrigger value="all" className="data-[state=active]:bg-orange-500">All</TabsTrigger>
              <TabsTrigger value="residential" className="data-[state=active]:bg-orange-500">Home</TabsTrigger>
              <TabsTrigger value="commercial" className="data-[state=active]:bg-blue-500">Business</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...products.residential, ...products.commercial].map((product, i) => (
                  <ProductCard key={`all-${product.name}-${i}`} product={product} index={i} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="residential">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.residential.map((product, i) => (
                  <ProductCard key={`res-${product.name}-${i}`} product={product} index={i} type="home" />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="commercial">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.commercial.map((product, i) => (
                  <ProductCard key={`com-${product.name}-${i}`} product={product} index={i} type="business" />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2 mb-6">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-sm text-yellow-300">5.0 Rating on Google</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Our <span className="text-orange-400">Customers Say</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Trusted by hundreds of homes and businesses across Cape Town
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah M.',
                type: 'Home Customer',
                rating: 5,
                text: 'Best gas supplier in Cape Town! Same-day delivery and the driver was so friendly. Been using them for 2 years now for our home.',
                date: 'December 2025'
              },
              {
                name: 'Ahmed K.',
                type: 'Restaurant Owner',
                rating: 5,
                text: 'We switched to North Energy for our restaurant and never looked back. Reliable bulk delivery, fair prices, and they actually answer the phone!',
                date: 'November 2025'
              },
              {
                name: 'Linda P.',
                type: 'Home Customer',
                rating: 5,
                text: 'Ordered via WhatsApp and had gas delivered within 2 hours. So convenient! The 19kg cylinder lasts our family about 6 weeks.',
                date: 'January 2026'
              },
              {
                name: 'Cape Kitchen Co.',
                type: 'Catering Business',
                rating: 5,
                text: 'Professional service from start to finish. They installed our commercial setup and maintain it quarterly. Highly recommend for businesses!',
                date: 'October 2025'
              },
              {
                name: 'John D.',
                type: 'Home Customer',
                rating: 5,
                text: 'Finally found a gas supplier I can trust. Fair prices, no hidden fees, and they even helped me choose the right cylinder size for my braai.',
                date: 'December 2025'
              },
              {
                name: 'Sunrise Hotel',
                type: 'Hospitality',
                rating: 5,
                text: 'Managing gas for a 50-room hotel is no joke. North Energy handles our bulk supply seamlessly. Emergency delivery saved us twice!',
                date: 'November 2025'
              }
            ].map((review) => (
              <motion.div
                key={review.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800/30 border-slate-700 h-full" data-testid={`review-${review.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 text-orange-400/30 mb-4" />
                    <p className="text-slate-300 mb-4 italic">"{review.text}"</p>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(review.rating)].map((_, j) => (
                        <Star key={`star-${j}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{review.name}</p>
                        <p className="text-xs text-slate-500">{review.type}</p>
                      </div>
                      <p className="text-xs text-slate-500">{review.date}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <a
              href="https://www.google.com/maps/place/North+Energy+and+Supplies/@-33.8412265,18.7083881,17z/data=!4m8!3m7!1s0x1dcc53cf1ce702f5:0x5e6dd6d07421a50f!8m2!3d-33.8412265!4d18.7083881!9m1!1b1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300"
            >
              See all reviews on Google Maps
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 sm:py-32 bg-slate-900/50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Get In <span className="text-orange-400">Touch</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Ready to order? Contact us today for fast delivery!
            </p>
          </motion.div>
          
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Address</h3>
                      <p className="text-slate-400">121 Darwin Rd, Windsor Park<br />Cape Town, 7570<br />South Africa</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Phone</h3>
                      <p className="text-slate-400">
                        <a href="tel:+27752679346" className="hover:text-orange-400">+27 75 267 9346</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Email</h3>
                      <p className="text-slate-400">info@northenergyandsupplies.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Business Hours</h3>
                      <p className="text-slate-400 text-sm">
                        <strong>Mon - Fri:</strong> 8:00 - 17:00<br />
                        <strong>Saturday:</strong> 8:00 - 13:00<br />
                        <strong>Sun & Holidays:</strong> Phone Only<br />
                        <span className="text-green-400">WhatsApp orders 24/7</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <a
                href="https://wa.me/27752679346"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-xl font-medium transition-colors w-full"
                data-testid="whatsapp-order-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp 24/7 Orders
              </a>
            </motion.div>
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-6">Send Us a Message</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-200">Name *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="bg-slate-900 border-slate-700 text-white"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-200">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-slate-900 border-slate-700 text-white"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-200">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="bg-slate-900 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customerType" className="text-slate-200">I am a...</Label>
                        <Select
                          value={formData.customerType}
                          onValueChange={(val) => setFormData({ ...formData, customerType: val })}
                        >
                          <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="contact-customer-type">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="home">Home Customer</SelectItem>
                            <SelectItem value="restaurant">Restaurant Owner</SelectItem>
                            <SelectItem value="hotel">Hotel/Hospitality</SelectItem>
                            <SelectItem value="factory">Factory/Industrial</SelectItem>
                            <SelectItem value="catering">Catering Business</SelectItem>
                            <SelectItem value="other">Other Business</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-200">Message *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white min-h-[120px]"
                        placeholder="Tell us what you need - cylinder size, delivery address, etc."
                        required
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6"
                      data-testid="contact-submit"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <div className="rounded-2xl overflow-hidden border border-slate-700">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3310.882631588885!2d18.7083881!3d-33.8412265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc53cf1ce702f5%3A0x5e6dd6d07421a50f!2sNorth%20Energy%20and%20Supplies!5e0!3m2!1sen!2sza!4v1647889012345!5m2!1sen!2sza"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="North Energy and Supplies Location"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_moltbot-config-1412/artifacts/8ym01w4n_WhatsApp%20Image%202026-03-17%20at%2017.42.51.jpeg" 
                  alt="North Energy and Supplies Logo"
                  className="w-14 h-14 rounded-lg object-contain bg-black"
                />
                <div>
                  <h3 className="font-bold text-white">North Energy</h3>
                  <p className="text-xs text-slate-400">& Supplies</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Quality LPG gas for homes and businesses in Cape Town since 2014.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">For Home</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>9kg Cylinders</li>
                <li>14kg Cylinders</li>
                <li>19kg Cylinders</li>
                <li>Gas Heaters</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">For Business</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>Bulk Delivery</li>
                <li>Commercial Installation</li>
                <li>Safety Compliance</li>
                <li>Account Management</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-400" />
                  121 Darwin Rd, Windsor Park
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-orange-400" />
                  +27 75 267 9346
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-400" />
                  info@northenergyandsupplies.com
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} North Energy and Supplies. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Product Card Component
function ProductCard({ product, index, type }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="bg-slate-800/50 border-slate-700 overflow-hidden group hover:border-orange-500/50 transition-colors h-full" data-testid={`product-card-${index}`}>
        <div className="aspect-square bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center relative overflow-hidden">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <Flame className="w-16 h-16 text-orange-400/50 group-hover:text-orange-400 transition-colors" />
          )}
          {product.bestSeller && (
            <span className="absolute top-3 right-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
              Best Seller
            </span>
          )}
          {product.popular && !product.bestSeller && (
            <span className="absolute top-3 right-3 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
              Popular
            </span>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-semibold text-white mb-1">{product.name}</h3>
          <p className="text-sm text-slate-400 mb-2">{product.desc}</p>
          <p className="text-orange-400 font-bold">{product.price}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
